import { makeAutoObservable, observable } from 'mobx';

import type {
  LayoutRepresentationBinding,
  LayoutRepresentationMap,
} from '@src/editor/models/layout/types';

export type SceneLayoutLinkRole = 'parent' | 'composition' | 'representation';

export type SceneLayoutLink = LayoutRepresentationBinding;

export interface SceneLayoutIndexRecord {
  link: SceneLayoutLink;
  role: SceneLayoutLinkRole;
}

export const TIME_EPSILON = 0.0001;

function isTimeInRange(
  absoluteTime: number,
  start: number | undefined,
  duration: number | undefined,
  epsilon: number,
  upperBound: 'inclusive' | 'exclusive' = 'inclusive',
): boolean {
  if (typeof start !== 'number' || !Number.isFinite(start)) {
    return false;
  }

  if (
    typeof duration === 'number' &&
    Number.isFinite(duration) &&
    duration > 0
  ) {
    if (absoluteTime < start - epsilon) {
      return false;
    }
    return upperBound === 'exclusive'
      ? absoluteTime < start + duration - epsilon
      : absoluteTime <= start + duration + epsilon;
  }
  // Duration-less: exact time match only
  return Math.abs(absoluteTime - start) < epsilon;
}

/**
 * Checks if the given absolute time falls within a link's compositionTime range,
 * i.e. the range of the **scene** the link belongs to.
 */
export function isTimeInLinkRange(
  link: SceneLayoutLink,
  absoluteTime: number,
  epsilon: number = TIME_EPSILON,
): boolean {
  return isTimeInRange(
    absoluteTime,
    link.compositionTime,
    link.compositionDuration,
    epsilon,
  );
}

/**
 * Checks if the given absolute time falls within the range of the **element**
 * the link's `representationId` names — narrower than the scene range whenever
 * one scene holds several representations.
 *
 * Half-open `[start, end)`, unlike the inclusive scene range: adjacent
 * representations share a boundary, and at exactly that time the forward part is
 * the one still on screen — and the only one the forward-only playback window
 * keeps, since `filterCompositionChildren` drops a child once
 * `start + duration > currentTime` fails. Matching the part behind the playhead
 * there would resolve to an id the source no longer contains (PLAT-11065).
 */
export function isTimeInRepresentationRange(
  link: SceneLayoutLink,
  absoluteTime: number,
  epsilon: number = TIME_EPSILON,
): boolean {
  return isTimeInRange(
    absoluteTime,
    link.representationTime,
    link.representationDuration,
    epsilon,
    'exclusive',
  );
}

/**
 * Finds a record whose compositionTime range contains the given absolute time.
 * Returns the first matching record, or undefined if none match.
 *
 * This is used for exact range matching (e.g., user keyframe sync) as opposed
 * to "best match by distance" logic in getRoleRecordFor.
 */
export function findRecordByTimeRange(
  records: SceneLayoutIndexRecord[],
  absoluteTime: number,
  epsilon: number = TIME_EPSILON,
): SceneLayoutIndexRecord | undefined {
  for (const record of records) {
    if (isTimeInLinkRange(record.link, absoluteTime, epsilon)) {
      return record;
    }
  }
  return undefined;
}

/**
 * Finds a link whose compositionTime range contains the given absolute time.
 * Returns the first matching link, or undefined if none match.
 */
export function findLinkByTimeRange(
  links: SceneLayoutLink[],
  absoluteTime: number,
  epsilon: number = TIME_EPSILON,
): SceneLayoutLink | undefined {
  for (const link of links) {
    if (isTimeInLinkRange(link, absoluteTime, epsilon)) {
      return link;
    }
  }
  return undefined;
}

/**
 * Finds a link whose **representation** range contains the given absolute time.
 * Links without a representation range never match, so a caller that tries this
 * first and falls back to {@link findLinkByTimeRange} keeps scene-range behaviour
 * for unstamped links while letting stamped ones win.
 */
export function findLinkByRepresentationTimeRange(
  links: SceneLayoutLink[],
  absoluteTime: number,
  epsilon: number = TIME_EPSILON,
): SceneLayoutLink | undefined {
  for (const link of links) {
    if (isTimeInRepresentationRange(link, absoluteTime, epsilon)) {
      return link;
    }
  }
  return undefined;
}

/**
 * SceneLayoutIndexStore keeps a sidecar map linking hidden parents, supporting compositions,
 * and visible representations produced by the layout engine. The editor hands us the pre-computed map
 * after each layout pass so selection logic stays decoupled from renderer payload heuristics.
 */
export class SceneLayoutIndexStore {
  _recordsByElementId: Map<string, SceneLayoutIndexRecord[]> = new Map();

  constructor() {
    makeAutoObservable(
      this,
      {
        _recordsByElementId: observable.ref,
      },
      { autoBind: true },
    );
  }

  get recordsByElementId(): ReadonlyMap<string, SceneLayoutIndexRecord[]> {
    return this._recordsByElementId;
  }

  updateFromLayoutRepresentationMap(
    map: LayoutRepresentationMap | null | undefined,
  ): void {
    if (!map || Object.keys(map).length === 0) {
      this.reset();
      return;
    }

    const nextMap = new Map<string, SceneLayoutIndexRecord[]>();

    for (const bindings of Object.values(map)) {
      if (!Array.isArray(bindings)) {
        continue;
      }

      for (const binding of bindings) {
        if (!binding) {
          continue;
        }
        this.registerLink(nextMap, binding);
      }
    }

    this._recordsByElementId = nextMap;
  }

  reset(): void {
    this._recordsByElementId = new Map();
  }

  dispose(): void {
    this.reset();
  }

  getRecords(elementId: string): SceneLayoutIndexRecord[] | undefined {
    return this._recordsByElementId.get(elementId);
  }

  getRoleRecordFor(
    elementId: string,
    targetRole: SceneLayoutLinkRole,
    referenceTime?: number,
  ): SceneLayoutIndexRecord | undefined {
    const records = this.getRecords(elementId);
    if (!records || records.length === 0) {
      return undefined;
    }
    const matching = records.filter(record => record.role === targetRole);
    if (matching.length === 0) {
      return undefined;
    }
    if (matching.length === 1 || referenceTime === undefined) {
      return matching[0];
    }
    // When multiple records share the same role (e.g. merged video children sharing
    // a single representationId across scenes), use time-range lookup to find exact match
    const rangeMatch = findRecordByTimeRange(matching, referenceTime);
    return rangeMatch;
  }

  getRepresentationRoleRecordFor(
    elementId: string,
    referenceTime?: number,
  ): SceneLayoutIndexRecord | undefined {
    return this.getRoleRecordFor(elementId, 'representation', referenceTime);
  }

  /**
   * Returns ALL representation records for a given element ID.
   * Used when an element is shared across multiple scenes (merged video children)
   * and the caller needs to evaluate all bindings rather than picking the best match.
   */
  getAllRepresentationRecordsFor(elementId: string): SceneLayoutIndexRecord[] {
    const records = this.getRecords(elementId);
    if (!records || records.length === 0) {
      return [];
    }
    return records.filter(record => record.role === 'representation');
  }

  /**
   * Find a representation record for an element whose compositionTime range
   * contains the given absolute time. Returns undefined if no exact match.
   *
   * This differs from getRoleRecordFor in that it requires the time to fall
   * strictly within the compositionTime range, rather than picking the
   * "closest" match by distance. Use this for precise scene identification
   * (e.g., user keyframe sync).
   */
  findRepresentationRecordByTimeRange(
    elementId: string,
    absoluteTime: number,
  ): SceneLayoutIndexRecord | undefined {
    const records = this.getAllRepresentationRecordsFor(elementId);
    if (records.length === 0) {
      return undefined;
    }
    return findRecordByTimeRange(records, absoluteTime);
  }

  /**
   * Resolve the visible representation id that should become active for the provided element id.
   * Returns the same id if the element is already the visible representation, or undefined if no
   * relationship is known.
   * When multiple representations exist, prefers unlocked ones over locked ones.
   */
  resolveVisibleRepresentationId(
    elementId: string,
    referenceTime?: number,
  ): string | undefined {
    const records = this._recordsByElementId.get(elementId);
    if (!records || records.length === 0) {
      return undefined;
    }

    if (records.some(record => record.role === 'representation')) {
      // Already pointing at the visible element.
      return elementId;
    }

    // Use composite key (representationId + sceneId) to preserve per-scene timing info
    // when multiple scenes share the same merged representation element
    const uniqueLinks = new Map<string, SceneLayoutLink>();
    for (const record of records) {
      const key = `${record.link.representationId}::${record.link.sceneId}`;
      uniqueLinks.set(key, record.link);
    }

    if (uniqueLinks.size === 0) {
      return undefined;
    }

    // Filter to unlocked links if any exist
    let candidateLinks = Array.from(uniqueLinks.values());
    const unlockedLinks = candidateLinks.filter(link => !link.locked);
    // Continue using locked element only if no unlocked ones exist
    if (unlockedLinks.length > 0) {
      candidateLinks = unlockedLinks;
    }

    if (candidateLinks.length === 1) {
      return candidateLinks[0]?.representationId;
    }

    if (referenceTime === undefined) {
      return candidateLinks[0]?.representationId;
    }

    // Prefer each representation's own range: when one scene holds several
    // representations (a fragmented track), every candidate carries that scene's
    // range and would match at any time, so scene matching can only ever return
    // the first candidate — the part at the head of the scene, which the
    // forward-only playback window is the first to prune (PLAT-11065). Falls
    // back to scene matching for links with no representation range.
    const rangeMatch =
      findLinkByRepresentationTimeRange(candidateLinks, referenceTime) ??
      findLinkByTimeRange(candidateLinks, referenceTime);
    return rangeMatch?.representationId ?? candidateLinks[0]?.representationId;
  }

  resolveParentId(elementId: string): SceneLayoutLink['parentId'] | undefined {
    return this._resolveFieldById(elementId, 'parentId');
  }

  resolveCompositionId(
    elementId: string,
  ): SceneLayoutLink['compositionId'] | undefined {
    return this._resolveFieldById(elementId, 'compositionId');
  }

  private _resolveFieldById<K extends keyof SceneLayoutLink>(
    elementId: string,
    field: K,
  ): SceneLayoutLink[K] | undefined {
    const records = this._recordsByElementId.get(elementId);
    if (!records || records.length === 0) {
      return undefined;
    }

    for (const record of records) {
      if (record.role === 'representation') {
        return record.link[field] ?? undefined;
      }
    }

    return undefined;
  }

  /**
   * Resolve the parent ID for an element at a specific time.
   * Returns the parentId from the record whose compositionTime range contains the given time.
   * If no time-specific match is found, falls back to the first available parentId.
   */
  resolveParentIdByTime(
    elementId: string,
    referenceTime: number,
    getElementTimeRange: (
      elementId: string,
    ) => { globalTime: number; duration: number } | undefined,
  ): string | undefined {
    const records = this._recordsByElementId.get(elementId);
    if (!records || records.length === 0) {
      return undefined;
    }

    // Get all representation records for this element
    const representationRecords = records.filter(
      record => record.role === 'representation',
    );

    if (representationRecords.length === 0) {
      return undefined;
    }

    // Find the parent element whose time range contains the reference time
    for (const record of representationRecords) {
      const parentId = record.link.parentId;
      if (!parentId) continue;

      const timeRange = getElementTimeRange(parentId);
      if (!timeRange) continue;

      const { globalTime, duration } = timeRange;
      if (
        referenceTime >= globalTime &&
        referenceTime <= globalTime + duration
      ) {
        return parentId;
      }
    }

    return undefined;
  }

  private registerLink(
    accumulator: Map<string, SceneLayoutIndexRecord[]>,
    link: SceneLayoutLink,
  ): void {
    this.addRecord(accumulator, link.parentId, {
      link,
      role: 'parent',
    });
    this.addRecord(accumulator, link.compositionId, {
      link,
      role: 'composition',
    });
    this.addRecord(accumulator, link.representationId, {
      link,
      role: 'representation',
    });
  }

  private addRecord(
    accumulator: Map<string, SceneLayoutIndexRecord[]>,
    elementId: string | null,
    record: SceneLayoutIndexRecord,
  ): void {
    if (!elementId) {
      return;
    }
    const existing = accumulator.get(elementId);
    if (existing) {
      existing.push(record);
      return;
    }
    accumulator.set(elementId, [record]);
  }
}

export default SceneLayoutIndexStore;
